import { getTickerAndMicFromContributor } from "helpers/securities"
import { get, chain, isEmpty, merge, omit, keyBy, mapValues } from "lodash"
import { createSelector } from "reselect"
import { buildContributorKey } from "helpers/contributors"
import { getSimulateActions } from "."

const EMPTY_OBJECT = {}
const EMPTY_ARRAY = []

// Need to duplicate this selector here to prevent dependency cycle
const getRawAssets = state => get(state, "global.data.simulatorComposition.data.model.simulation", EMPTY_OBJECT)
export const getRawPositionHoldings = state => get(state, "global.data.simulatorPositionHoldings.data.model.simulation.positions", EMPTY_OBJECT)
const getRebalancedRawAssets = state => get(state, "modules.rebalance.rebalancedPositions")
export const getRebalancedAssetHoldings = state => get(state, "modules.rebalance.rebalancedPositionHoldings")
const getRawGroupedAssets = state => get(state, "global.data.simulatorGroupedComposition.data.model.simulation", EMPTY_OBJECT)
const getRawGroupedRebalancedAssets = state => get(state, "modules.rebalance.rebalancedGroupedPositions")
export const getPositionsDelta = state => get(state, "modules.rebalance.rebalancedPositionsDelta", EMPTY_ARRAY)

export const getRawComposition = state => get(state, "global.data.simulatorComposition.data.model.simulation.composition.0", EMPTY_OBJECT)
export const getEquityOverride = state => get(state, "global.data.simulatorComposition.data.model.simulation.composition.0.referenceEquity")
export const getIncludeNotHeld = state => get(state, "modules.rebalance.includeNotHeld")
export const getGMV = state => get(state, "global.data.simulatorComposition.data.model.simulation.composition.0.gmv")
export const getReferenceEquity = state => get(state, "global.data.simulatorComposition.data.model.simulation.composition.0.referenceEquity")

const getHasBeenRebalanced = createSelector(
  [getSimulateActions],
  (actions) => !isEmpty(actions),
)

export const getWorkingEquity = createSelector(
  [getRawComposition],
  ({ referenceEquity, gmv }) => referenceEquity || gmv,
)

function processComposition(dollarAssets, percentAssets, grouped) {
  const keyedDollar = keyBy(dollarAssets, buildContributorKey)
  const keyedPercent = keyBy(percentAssets, buildContributorKey)

  return mapValues(keyedDollar, (asset, id) => ({
    ...omit(asset, "contributors"),
    ...(grouped && {
      isGroup: true,
      label: asset.description,
    }),
    ...getTickerAndMicFromContributor(asset),
    economicExposure: asset.isNotHeld ? undefined : asset.economicExposure,
    ...chain(keyedPercent)
      .get(id)
      .pick(["activePercentEquity", "positionPercentEquity", "basePercentEquity"])
      .value(),
    contributors: processComposition(asset.contributors, get(keyedPercent, [id, "contributors"])),
  }))
}

function getRemovedPositionsOrGroups(originalPositions, rebalancedPositions) {
  return chain(originalPositions)
    .map((positionOrGroup, id) => {
      let contributors
      // If the position has contributors (group), we need to check if any of them have been removed
      if (positionOrGroup.contributors) {
        contributors = getRemovedPositionsOrGroups(positionOrGroup.contributors, get(rebalancedPositions, [id, "contributors"]))
      }
      // Left side: If the position/group does not exist in the rebalanced positions, it was removed
      // Right side: If a position has been removed but is part of a group, we need to keep the group
      if (!get(rebalancedPositions, id) || !isEmpty(contributors)) {
        return {
          ...positionOrGroup,
          economicExposure: 0,
          positionPercentEquity: 0,
          activePercentEquity: -get(positionOrGroup, "basePercentEquity"),
          ...(!isEmpty(contributors) && { contributors }),
        }
      }
      return null
    })
    .compact()
    .keyBy(buildContributorKey)
    .value()
}

const getOriginalAssets = createSelector(
  [getRawAssets],
  (assets) => processComposition(...extractContributors(assets)),
)
const getGroupedOriginalAssets = createSelector(
  [getRawGroupedAssets],
  (groupedAssets) => processComposition(...extractContributors(groupedAssets, true)),
)

const getRebalancedAssets = createSelector(
  [getRebalancedRawAssets],
  computeRebalancedAssets(false),
)

const getGroupedRebalancedAssets = createSelector(
  [getRawGroupedRebalancedAssets],
  computeRebalancedAssets(true),
)

export const getPortfolioPositions = createSelector(
  [getOriginalAssets],
  computePositions,
)

export const getGroupedPortfolioPositions = createSelector(
  [getGroupedOriginalAssets],
  computePositions,
)

export const getRebalancedPortfolioPositions = createSelector(
  [getRebalancedAssets, getPortfolioPositions, getHasBeenRebalanced],
  computeRebalancedPositions,
)

export const getGroupedRebalancedPortfolioPositions = createSelector(
  [getGroupedRebalancedAssets, getGroupedPortfolioPositions, getHasBeenRebalanced],
  computeRebalancedPositions,
)

function computeRebalancedAssets(grouped) {
  return (composition) => {
    // Otherwise it thinks rebalance was triggered but no positions were returned
    if (isEmpty(composition)) {
      return null
    }
    return processComposition(...extractContributors(composition, grouped))
  }
}

function computeRebalancedPositions(rebalancedAssets, originalAssets, hasBeenRebalanced) {
  if (!hasBeenRebalanced) {
    return null
  }
  return computePositions(rebalancedAssets, { rebalanced: hasBeenRebalanced, originalPositions: originalAssets })
}

function extractContributors(composition, grouped) {
  let path = {
    dollar: ["composition", 0, "positions"],
    percent: ["percent", 0, "positions"],
  }
  if (grouped) {
    path = {
      dollar: ["groupedComposition", 0, "compositionBy"],
      percent: ["groupedPercent", 0, "compositionBy"],
    }
  }
  return [
    get(composition, path.dollar),
    get(composition, path.percent),
    grouped,
  ]
}

function computePositions(allPositions, options = {}) {
  if (!allPositions) { return EMPTY_OBJECT }

  const { rebalanced, originalPositions } = options ?? {}
  return chain(allPositions)
    .thru(positions => {
      if (!rebalanced) {
        return positions
      }

      // Zeroed out positions will not show up in the rebalanced query but must still be part of the positions object
      // so we fetch it from the original positions object and zero out the position
      const positionsIncludingRemoved = getRemovedPositionsOrGroups(originalPositions, positions)
      return merge(positionsIncludingRemoved, positions)
    })
    .value()
}
